import { CreateCustomerDialog } from "../app/components/patterns/CreateCustomerDialog";
import { useCanEditCustomers } from "../app/auth/hooks";
import type { CisCustomer } from "@shared-core/data/api-types";
import { buildCatalogueConfiguration } from "@shared-core/domain/catalogue-configuration";
import {
  formatDimensionsValue,
  resolveCustomValueLadderConfiguration,
} from "@shared-core/domain/catalogue-custom-value";
import { useCatalogueCustomValueLadder } from "@shared-core/domain/catalogue-custom-value-ladder";
import {
  VERSIONS_FIELD_KEY,
  quantityStepOf,
  versionCountOf,
  versionsFieldMessages,
  versionsProblem,
  versionsRequestFields,
  type VersionSplitMode,
  type VersionsValue,
} from "@shared-core/domain/line-versions";
import {
  extractFieldErrors,
  type FieldError,
} from "@shared-core/data/validation";
import {
  applyReorderToItemConfiguration,
  reorderFieldResolutionsByKey,
  START_NUMBER_FIELD_KEY,
} from "@shared-core/domain/applyReorderToItemConfiguration";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams, useLocation, Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ItemStartNumber,
  PreparedReorder,
} from "@shared-core/data/api-types";
import { deliverySelectionBody } from "@shared-core/data/delivery-selection-body";
import { hasSplitAddress } from "@shared-core/domain/delivery-selection";
import type {
  Catalogue,
  CatalogueField,
  CatalogueItem,
} from "@shared-core/data/catalogue-types";
import { CatalogueCustomerContext } from "../app/components/domain/catalogue/CatalogueCustomerContext";
import { useCatalogueContext } from "../app/components/domain/catalogue/useCatalogueContext";
import { ReorderProvenanceBanner } from "../app/components/domain/catalogue/ReorderProvenanceBanner";
import {
  ItemSpecification,
  specRecap,
} from "../app/components/domain/catalogue/ItemSpecification";
import {
  ItemQuantityLadder,
  levelPence,
  perUnitLabel,
  type ItemPricingState as PricingState,
} from "../app/components/domain/catalogue/ItemQuantityLadder";
import { ReorderFieldChip } from "../app/components/domain/catalogue/ReorderFieldChip";
import { StartNumberPanel } from "../app/components/domain/catalogue/StartNumberPanel";
import { RepeatableItemArtworkNote } from "../app/components/domain/artwork/RepeatableItemArtworkNote";
import type { ItemArtworkValidity } from "@shared-core/data/artwork-staff-types";
import {
  deriveAspect,
  fulfilmentBadge,
  isFinishedGoods,
  type FulfilmentTone,
} from "@shared-core/domain/catalogue-presentation";
import {
  cameFromTharsternText,
  migratedItemLine,
} from "@shared-core/domain/catalogue-migration-facts";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { Card } from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { useCanCurateCatalogue } from "../app/auth/hooks";
import { catalogueIcon } from "../app/components/domain/catalogue/catalogueIcons";
import { CostFreshnessIndicator } from "../app/components/domain/catalogue/CostFreshnessIndicator";
import { PriceBreakdownPanel } from "../app/components/domain/catalogue/PriceBreakdownPanel";
import { SpeedList } from "../app/components/domain/catalogue/SpeedList";
import {
  askUntilPriced,
  pendingRouteLine,
} from "@shared-core/domain/price-still-coming";
import {
  type CataloguePriceOptions,
  type PriceOptionChoice,
  type RouteOverrideReason,
} from "@shared-core/data/supplier-pricing-types";
import {
  findOption,
  priceOptionsNotice,
  type PriceOptionsNotice,
} from "@shared-core/domain/supplier-pricing-presentation";
import { PriceLevelPicker } from "../app/components/domain/pricing/PriceLevelPicker";
import type { PriceAdjustmentReason } from "@shared-core/data/catalogue-types";
import type {
  PrecedentCandidate,
  PriceLevelKind,
  PriceLevels,
} from "@shared-core/data/price-precedent-types";
import { withLinkedPrecedent } from "@shared-core/domain/price-precedent";
import { CataloguePreview, type PreviewVariant } from "./CataloguePreview";
import { DeliverySection } from "../app/components/domain/delivery/DeliverySection";
import type { DeliverySelection } from "@shared-core/data/delivery-types";
import {
  ArrowLeft,
  ExternalLink,
  Pencil,
  Loader2,
  AlertTriangle,
  ChevronDown,
  Mail,
  UserPlus,
} from "lucide-react";

// CatalogueItemPage — the configurator in "use" mode. One adaptive page driven
// by the item's field modes, with a sticky price + action bar. Choosing a spec
// updates a live, instant price (standard at a break, conservative per-unit
// between breaks). Quoting and ordering the priced item are One's: it goes
// onto a draft quote there.

const TONE_VARIANT: Record<FulfilmentTone, "neutral" | "success" | "warning"> =
  {
    made: "neutral",
    bought: "warning",
  };

function defaultValueFor(field: CatalogueField): string {
  if (field.mode === "fixed") return field.fixedValue ?? "";
  const def = field.options?.find((o) => o.isDefault) ?? field.options?.[0];
  if (def) return def.code;
  return field.defaultValue ?? "";
}

type BreakdownKind = "base" | "add" | "subtotal" | "discount";

function BreakdownLine({
  label,
  amount,
  kind = "base",
}: {
  label: string;
  amount: number;
  kind?: BreakdownKind;
}) {
  const sign = kind === "discount" ? "−" : kind === "add" ? "+" : "";
  return (
    <div
      className={`flex items-center justify-between ${kind === "subtotal" ? "font-medium" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`tabular-nums ${kind === "discount" ? "text-[color:var(--color-success)]" : ""}`}
      >
        {sign}
        {formatCurrencyGbp(Math.abs(amount))}
      </span>
    </div>
  );
}

// ── Config state ────────────────────────────────────────────────────────────

interface CustomerPick {
  code: string;
  name: string;
}

interface ContactPick {
  id: string;
  name: string;
  email: string;
  isPrimary?: boolean;
}

interface ConfigState {
  quantity: number;
  customQty: boolean;
  values: Record<string, string>;
  custom: Record<string, string>;
  multi: Record<string, string[]>;
  /** How a line of several versions shares its quantity (the count is `custom["Versions"]`), and the quantities as typed. */
  versionSplit: VersionSplitMode;
  versionsEntered: (number | null)[];
  /** The Speed the CSR chose (Time's code); null = the default Speed (d59). */
  speedCode: string | null;
  /** ISO date the order is needed by, and whether it is fixed (N3: a date, not "within N days"). */
  neededBy: string | null;
  neededByIsHard: boolean;
  /** Whether this is for a new prospect or an existing customer. */
  customerMode: "new" | "existing";
  /** Chosen existing customer (null until selected). */
  selectedCustomer: CustomerPick | null;
  /** Chosen contact at the customer (null until selected). */
  selectedContact: ContactPick | null;
  /**
   * "existing" = pick from the customer's contact list;
   * "new" = type in name + email for a contact not yet in the system.
   */
  contactMode: "existing" | "new";
  /** Contact captured directly, including a newly created customer. */
  newContactName: string;
  newContactEmail: string;
  /** Delivery method and address: settled before the item can be added, ordered or quoted. */
  delivery: DeliverySelection | null;
  // Price level (price-precedent hierarchy, decision 2026-09-15). null =
  // follow the preview's suggestion; the CSR's explicit pick otherwise. The
  // adjustment the write path records is derived: chosen price − policy price.
  priceLevel: PriceLevelKind | null;
  /** The custom level's price in pence; null until typed. */
  customPricePence: number | null;
  /** Why the price differs from policy; `held_at_last_price` is the default
   *  when the last ordered level is chosen. */
  priceAdjustmentReason: PriceAdjustmentReason | null;
  /** A past job or estimate the CSR linked by hand when the engine did not
   *  recognise a precedent; presented as a last ordered / last quoted level. */
  linkedPrecedent: PrecedentCandidate | null;
  /**
   * Third-party fulfilment, phase 2 (#82–#90). Only used while the preview
   * carries price options.
   *
   * `priceOption`: the CSR's explicit pick behind the Speed; null = the
   * Speed's own option.
   */
  priceOption: PriceOptionChoice | null;
  routeOverrideReason: RouteOverrideReason | null;
  routeOverrideNote: string;
}

function initialState(
  item: CatalogueItem,
  preparedReorder?: PreparedReorder | null,
): ConfigState {
  const values: Record<string, string> = {};
  const custom: Record<string, string> = {};
  for (const f of item.fields) {
    if (f.mode === "always_custom") {
      // Seed a sensible panel size so the preview + area read sensibly from
      // the start. Canonical "{w} x {h}" wire format — see
      // `formatDimensionsValue` — not the display-only "× … mm" the preview
      // renders.
      if (f.control === "dimension")
        custom[f.key] = formatDimensionsValue(600, 400);
      continue;
    }
    if (f.mode === "fixed") continue;
    values[f.key] = defaultValueFor(f);
  }

  let quantity = item.defaultQuantity;
  let selectedCustomer: CustomerPick | null = null;

  // Order again pre-fill (catalogue-reorder brief §16). Guarded on item id so a
  // stale location.state from a previous navigation (e.g. the CSR picked a
  // different item after landing) is never silently applied to the wrong
  // item's fields.
  if (preparedReorder && preparedReorder.item_id === item.id) {
    const applied = applyReorderToItemConfiguration(
      preparedReorder.field_resolutions ?? [],
      item.fields,
    );
    // The reorder ladder is authoritative for every field it resolved —
    // overwrite the schema defaults seeded above rather than merge under them.
    Object.assign(values, applied.values);
    Object.assign(custom, applied.custom);
    // An unresolved field renders as "no selection", not the schema default
    // the loop above just seeded — the whole point of tier 3 is that
    // nothing can misprice until the CSR picks a current value.
    for (const key of applied.unresolvedFieldKeys) {
      delete values[key];
      delete custom[key];
    }
    quantity = preparedReorder.quantity || item.defaultQuantity;
    if (preparedReorder.customer_code) {
      selectedCustomer = {
        code: preparedReorder.customer_code,
        name: preparedReorder.customer_name ?? preparedReorder.customer_code,
      };
    }
  }

  return {
    quantity,
    customQty: false,
    values,
    custom,
    multi: {},
    versionSplit: "even",
    versionsEntered: [],
    speedCode: null,
    neededBy: null,
    neededByIsHard: false,
    customerMode: "existing",
    selectedCustomer,
    selectedContact: null,
    contactMode: "existing",
    newContactName: "",
    newContactEmail: "",
    delivery: null,
    priceLevel: null,
    customPricePence: null,
    priceAdjustmentReason: null,
    linkedPrecedent: null,
    priceOption: null,
    routeOverrideReason: null,
    routeOverrideNote: "",
  };
}

// Thin resolver: look the item up and remount the configurator per item so its
// state starts fresh (react-router reuses the component across :itemId changes,
// so a keyed child is the clean reset).
export function CatalogueItemPage() {
  const { itemId } = useParams();
  const adapter = useDataAdapter();
  const location = useLocation();
  const [resolved, setResolved] = useState<
    | {
        catalogue: Catalogue;
        item: CatalogueItem;
      }
    | null
    | "loading"
  >("loading");
  const [loadError, setLoadError] = useState(false);
  const loadRequest = useRef(0);

  const load = useCallback(() => {
    const request = ++loadRequest.current;
    if (!itemId) {
      setResolved(null);
      return;
    }
    setResolved("loading");
    setLoadError(false);
    adapter
      .getResolvedCatalogueItem(itemId)
      .then((result) => {
        if (request === loadRequest.current) setResolved(result);
      })
      // A rejection (dropped connection, 500, 403) is not the same fact as
      // the adapter resolving null (genuinely no such item) — conflating
      // them told the CSR an item didn't exist when it was really a
      // transient failure (catalogue UX audit finding 7).
      .catch(() => {
        if (request === loadRequest.current) setLoadError(true);
      });
  }, [adapter, itemId]);

  useEffect(() => {
    load();
    const request = loadRequest.current;
    return () => {
      loadRequest.current = request + 1;
    };
  }, [load]);

  // Set from location.state when navigating here via a reorder pick
  // (catalogue-reorder brief §16) — the response prepareCatalogueReorder
  // already resolved. Only trusted for the item it actually names; a stale
  // state entry (browser back to a different item) is otherwise ignored.
  const preparedReorder =
    (location.state as { preparedReorder?: PreparedReorder } | null)
      ?.preparedReorder ?? null;

  if (loadError) {
    return (
      <div className="space-y-4">
        <Link
          to={`${paths.catalogue()}${location.search}`}
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)]"
        >
          <ArrowLeft className="size-4" /> Back to catalogue
        </Link>
        <Card className="p-12 text-center space-y-3">
          <p className="text-muted-foreground">
            Catalogue item could not be loaded. This could be a dropped
            connection or a server error.
          </p>
          <Button variant="outline" size="sm" onClick={load}>
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  if (resolved === "loading") {
    return (
      <div className="space-y-4">
        <Link
          to={`${paths.catalogue()}${location.search}`}
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)]"
        >
          <ArrowLeft className="size-4" /> Back to catalogue
        </Link>
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!resolved) {
    return (
      <div className="space-y-4">
        <Link
          to={`${paths.catalogue()}${location.search}`}
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)]"
        >
          <ArrowLeft className="size-4" /> Back to catalogue
        </Link>
        <Card className="p-12 text-center text-muted-foreground">
          Catalogue item not found.
        </Card>
      </div>
    );
  }

  return (
    <Configurator
      key={resolved.item.id}
      item={resolved.item}
      catalogue={resolved.catalogue}
      preparedReorder={
        preparedReorder?.item_id === resolved.item.id ? preparedReorder : null
      }
    />
  );
}

// ── Contact selection ────────────────────────────────────────────────────────

function ContactSection({
  state,
  setState,
  onCustomerSaved,
  createCustomerOpen,
  onCreateCustomerOpenChange,
}: {
  createCustomerOpen: boolean;
  onCreateCustomerOpenChange: (open: boolean) => void;
  state: ConfigState;
  setState: (next: Partial<ConfigState>) => void;
  /** Reports a customer just created in CIS up to the page, which switches to it. */
  onCustomerSaved: (customer: CisCustomer) => void;
}) {
  const adapter = useDataAdapter();
  // Contact search state
  const [contactQuery, setContactQuery] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactResults, setContactResults] = useState<ContactPick[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const contactInputRef = useRef<HTMLInputElement>(null);

  // Load contacts when customer changes or contact search query changes
  useEffect(() => {
    if (!state.selectedCustomer) {
      setContactResults([]);
      setContactsLoading(false);
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    setContactResults([]);
    adapter
      .getContactViews({
        customerId: state.selectedCustomer.code,
        q: contactQuery || undefined,
        pageSize: 50,
      })
      .then((r) => {
        if (cancelled) return;
        setContactResults(
          r.items.map((c) => ({
            id: c.id,
            name: c.name ?? "",
            email: c.email_address ?? "",
            isPrimary: c.is_primary_customer_contact ?? false,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setContactResults([]);
      })
      .finally(() => {
        if (!cancelled) setContactsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.selectedCustomer, contactQuery, adapter]);

  return (
    <Card className="p-5 space-y-4">
      <h2 className="text-sm font-medium">Contact</h2>
      {!state.selectedCustomer && (
        <p className="text-sm text-muted-foreground">
          Select a customer above to choose a contact.
        </p>
      )}

      {state.customerMode === "existing" && (
        <div className="space-y-3">
          {/* Contact picker — shown once a customer is chosen */}
          {state.selectedCustomer && state.contactMode === "existing" && (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">
                Select contact
              </div>
              <div className="relative">
                <button
                  type="button"
                  aria-label="Select contact"
                  aria-expanded={contactOpen}
                  className="flex w-full text-left items-center gap-2 rounded-md border border-border bg-card px-3 py-2 cursor-pointer"
                  onClick={() => {
                    setContactOpen(true);
                    setTimeout(() => contactInputRef.current?.focus(), 0);
                  }}
                >
                  {contactsLoading ? (
                    <Loader2 className="size-4 shrink-0 text-muted-foreground animate-spin" />
                  ) : (
                    <Mail className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  {state.selectedContact ? (
                    <span className="flex-1 text-sm">
                      <span className="font-medium">
                        {state.selectedContact.name}
                      </span>
                      <span className="ml-1.5 text-muted-foreground">
                        {state.selectedContact.email}
                      </span>
                    </span>
                  ) : (
                    <span className="flex-1 text-sm text-muted-foreground">
                      Search contacts…
                    </span>
                  )}
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                </button>

                {contactOpen && (
                  <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
                    <div className="p-2 border-b border-border">
                      <input
                        ref={contactInputRef}
                        autoFocus
                        value={contactQuery}
                        onChange={(e) => setContactQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setContactOpen(false);
                            setContactQuery("");
                          }
                        }}
                        placeholder="Search by name or email…"
                        className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {contactsLoading && (
                        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                          <Loader2 className="size-3.5 animate-spin" />
                          Loading contacts…
                        </div>
                      )}
                      {!contactsLoading &&
                        contactResults.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setState({ selectedContact: c });
                              setContactOpen(false);
                              setContactQuery("");
                            }}
                            className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted"
                          >
                            <span className="flex items-center gap-1.5">
                              {c.name}
                              {c.isPrimary && (
                                <span className="text-xs text-muted-foreground">
                                  · primary
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {c.email}
                            </span>
                          </button>
                        ))}
                      {!contactsLoading && contactResults.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No contacts found
                        </div>
                      )}
                      {/* New contact escape hatch */}
                      <div className="border-t border-border mt-1 pt-1">
                        <button
                          onClick={() => {
                            setState({
                              contactMode: "new",
                              selectedContact: null,
                              newContactName: "",
                              newContactEmail: "",
                            });
                            setContactOpen(false);
                            setContactQuery("");
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[color:var(--color-primary)] hover:bg-muted"
                        >
                          <UserPlus className="size-4 shrink-0" />
                          New contact for {state.selectedCustomer.name}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* New contact form — shown when rep wants to send to someone not in the system */}
          {state.selectedCustomer && state.contactMode === "new" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-muted-foreground">
                  New contact for {state.selectedCustomer.name}
                </div>
                <button
                  onClick={() =>
                    setState({
                      contactMode: "existing",
                      newContactName: "",
                      newContactEmail: "",
                    })
                  }
                  className="text-xs text-muted-foreground hover:text-muted-foreground"
                >
                  ← Back to contacts
                </button>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">
                  Contact name
                </label>
                <input
                  type="text"
                  value={state.newContactName}
                  onChange={(e) => setState({ newContactName: e.target.value })}
                  placeholder="e.g. Jane Smith"
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">
                  Contact email
                </label>
                <input
                  type="email"
                  value={state.newContactEmail}
                  onChange={(e) =>
                    setState({ newContactEmail: e.target.value })
                  }
                  placeholder="jane@example.com"
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {createCustomerOpen && (
        <CreateCustomerDialog
          open={createCustomerOpen}
          onOpenChange={onCreateCustomerOpenChange}
          onCreated={onCustomerSaved}
        />
      )}
    </Card>
  );
}

// ── Preview / pricing / delivery derivations ───────────────────────────────

function previewVariant(
  item: CatalogueItem,
  hasSections: boolean,
): PreviewVariant {
  if (isFinishedGoods(item)) return "product";
  if (hasSections) return "booklet";
  if (item.id.includes("ncr")) return "stack";
  return "sheet";
}

function isMonoPrint(item: CatalogueItem, state: ConfigState): boolean {
  const print = item.fields.find((f) => f.key === "Print" && !f.section);
  if (!print) return false;
  const opt = print.options?.find((o) => o.code === state.values["Print"]);
  const sig = `${opt?.submit ?? ""} ${opt?.label ?? ""}`.toLowerCase();
  return sig.includes("mono") || /\b1\/[01]\b/.test(sig);
}

/**
 * Levels for a preview that carries none (the route today): policy only, so
 * the picker — and with it the custom price — is always there. Precedent
 * arrives with the preview once the backend serves it.
 */
function policyOnlyLevels(policyPence: number, quantity: number): PriceLevels {
  return {
    suggested: "policy",
    unrecognised: false,
    levels: [
      {
        kind: "policy",
        price_pence: policyPence,
        unit_price_pence: quantity > 0 ? policyPence / quantity : 0,
        source: null,
        contribution_margin_pct: null,
        verdict: "unchecked",
        verdict_reason: null,
        suggestable: true,
        not_suggestable_reason: null,
      },
    ],
    work_style: "other",
    work_style_floor_pct: null,
    drop_to_policy: null,
  };
}

/**
 * A repeatable item's artwork today and what ordering does with it (AW3c-4),
 * or null for a configurable item (which is never asked about). A failed read
 * shows nothing: ordering does not depend on it. Supplying and confirming the
 * artwork are the item's One commands.
 */
function useRepeatableItemArtwork(
  item: CatalogueItem,
): ItemArtworkValidity | null {
  const adapter = useDataAdapter();
  const [validity, setValidity] = useState<ItemArtworkValidity | null>(null);
  const isRepeatable = item.kind === "repeatable";
  useEffect(() => {
    setValidity(null);
  }, [item.id]);
  useEffect(() => {
    if (!isRepeatable) return;
    let isCurrent = true;
    adapter
      .getRepeatableItemArtwork(item.id)
      .then((read) => {
        if (isCurrent) setValidity(read?.validity ?? null);
      })
      .catch(() => {
        if (isCurrent) setValidity(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [adapter, item.id, isRepeatable]);
  return validity;
}

/** A numbered item's start-number state (NCR sets, pads and books), read only
 * for an item with the Start number field, and its opening-number entry. */
function useItemStartNumber(
  itemId: string,
  hasStartNumberField: boolean,
): {
  state: ItemStartNumber | null;
  saving: boolean;
  error: string | null;
  saveOpening: (value: string) => void;
} {
  const adapter = useDataAdapter();
  const [state, setState] = useState<ItemStartNumber | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setState(null);
    setError(null);
    if (!hasStartNumberField) return;
    let isCurrent = true;
    adapter
      .getCatalogueItemStartNumber(itemId)
      .then((read) => {
        if (isCurrent) setState(read);
      })
      .catch(() => {
        // Optional enrichment: without it the page still takes a typed start number.
        if (isCurrent) setState(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [adapter, itemId, hasStartNumberField]);
  const saveOpening = (value: string) => {
    setSaving(true);
    setError(null);
    adapter
      .setCatalogueItemOpeningStartNumber(itemId, value.trim() || null)
      .then(setState)
      .catch((err: unknown) => {
        const fieldError = extractFieldErrors(err).find(
          (e) => e.field === "opening_start_number",
        );
        setError(
          fieldError?.message ?? "The opening start number wasn't saved.",
        );
      })
      .finally(() => setSaving(false));
  };
  return { state, saving, error, saveOpening };
}

function Configurator({
  item,
  catalogue,
  preparedReorder,
}: {
  item: CatalogueItem;
  catalogue: Catalogue;
  /** Set when landing here via a reorder pick (catalogue-reorder brief §16),
   *  already matched to `item.id` by the caller. */
  preparedReorder?: PreparedReorder | null;
}) {
  const adapter = useDataAdapter();
  const canOpenProvenanceLinks = useCanCurateCatalogue();
  const context = useCatalogueContext(
    preparedReorder?.customer_code
      ? {
          code: preparedReorder.customer_code,
          name: preparedReorder.customer_name ?? preparedReorder.customer_code,
        }
      : null,
  );
  const [state, setStateRaw] = useState<ConfigState>(() => ({
    ...initialState(item, preparedReorder),
    selectedCustomer: context.customer,
  }));
  const itemArtwork = useRepeatableItemArtwork(item);
  const [customerChanged, setCustomerChanged] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const canCreateCustomer = useCanEditCustomers();
  // The URL is authoritative; a newly saved prospect also carries its contact
  // until the customer replica catches up. Apply that patch with the URL change.
  const [pendingCustomerPatch, setPendingCustomerPatch] =
    useState<Partial<ConfigState> | null>(null);
  if ((state.selectedCustomer?.code ?? "") !== (context.customer?.code ?? "")) {
    const pending = pendingCustomerPatch;
    setPendingCustomerPatch(null);
    setStateRaw((previous) => ({
      ...previous,
      customerMode: "existing",
      selectedCustomer: context.customer,
      selectedContact: null,
      contactMode: "existing",
      newContactName: "",
      newContactEmail: "",
      delivery: null,
      priceLevel: null,
      customPricePence: null,
      priceAdjustmentReason: null,
      linkedPrecedent: null,
      ...(pending?.selectedCustomer?.code === context.customer?.code
        ? pending
        : {}),
    }));
    setCustomerChanged(true);
  }
  const setState = (next: Partial<ConfigState>) => {
    if (
      "selectedCustomer" in next &&
      next.selectedCustomer?.code !== state.selectedCustomer?.code
    ) {
      setPendingCustomerPatch(next);
      context.selectCustomer(next.selectedCustomer ?? null);
      return;
    }
    setStateRaw((s) => ({ ...s, ...next }));
  };
  const itemAvailable =
    !context.loading &&
    !context.failed &&
    context.catalogues.some((c) => c.id === catalogue.id);
  // The reorder ladder's tier-"unresolved" field keys, fixed at mount (the
  // set of fields that started unresolved never changes size — a CSR fills
  // one in, it doesn't grow back). Recomputed live against current state
  // below to know which are *still* unresolved.
  const reorderInitialUnresolvedKeys = useMemo(
    () =>
      preparedReorder
        ? applyReorderToItemConfiguration(
            preparedReorder.field_resolutions ?? [],
            item.fields,
          ).unresolvedFieldKeys
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computed once at mount; preparedReorder/item are stable for this Configurator instance (keyed by item.id).
    [],
  );
  const reorderFieldResolutions = useMemo(
    () =>
      preparedReorder
        ? reorderFieldResolutionsByKey(preparedReorder.field_resolutions ?? [])
        : null,
    [preparedReorder],
  );
  const hasStartNumberField = item.fields.some(
    (f) => f.key === START_NUMBER_FIELD_KEY,
  );
  const itemStartNumber = useItemStartNumber(item.id, hasStartNumberField);
  const startNumberProposal =
    preparedReorder?.start_number ?? itemStartNumber.state?.next_start ?? null;
  // Still-unresolved subset — a field leaves this list the moment the CSR
  // gives it any value, preset or custom. No price shows while it's
  // non-empty (brief §16: "nothing can misprice").
  const reorderUnresolvedFieldKeys = reorderInitialUnresolvedKeys.filter(
    (key) => !state.values[key] && !state.custom[key],
  );

  const Icon = catalogueIcon(item.icon);
  const badge = fulfilmentBadge(item);
  const migratedLine = migratedItemLine({
    catalogueName: catalogue.name,
    customerProductRef: item.customerProductRef,
    routeHint: item.routeHint,
  });

  // Group fields into job-level + named sections (multi-part items).
  const jobFields = item.fields.filter((f) => !f.section);
  const sections = item.fields.reduce<Record<string, CatalogueField[]>>(
    (acc, f) => {
      if (!f.section) return acc;
      (acc[f.section] ??= []).push(f);
      return acc;
    },
    {},
  );
  const sectionNames = item.fields
    .map((f) => f.section)
    .filter((s, i, arr): s is string => !!s && arr.indexOf(s) === i);

  const backToCatalogue = `${paths.catalogue()}${context.search ? `?${context.search}` : ""}`;

  // Live-preview + summary derivations.
  const allFields = jobFields.concat(sectionNames.flatMap((n) => sections[n]));
  const hasSections = sectionNames.length > 0;
  const finishing = state.multi["Finishing"] ?? [];
  const preview = {
    variant: previewVariant(item, hasSections),
    aspect: deriveAspect(item, state),
    mono: isMonoPrint(item, state),
    rounded: finishing.includes("round"),
    laminated: finishing.includes("lam"),
  };
  const recap = specRecap(allFields, state);

  // Order-time price. catalogue_module's price-preview route is the *only*
  // resolution path that reads the selected variant's real Tharstern
  // manufacturing cost, so it is the single source of every price on this
  // screen — the headline *and* the quantity ladder, which is why the response
  // covers the whole ladder. The generic pricing/quote route never sees that
  // cost and must not drive a number a user reads as a price. Customer code is
  // optional: absent, the backend prices against the central-scope price group,
  // which is a real answer because the cost is keyed on variant × quantity, not
  // on the customer.
  //
  // There is no seeded initial value and no fallback. A failed or superseded
  // resolution renders an explicit error and *no* price: a mock price, or a
  // price left over from a previous configuration, is indistinguishable from a
  // real one on screen, which is exactly how a month of 400s went unnoticed.
  const previewCustomerCode = context.customer?.code ?? null;
  // The line's delivery choice, once there is one to price: a shipment with
  // its address (one with none is the CSR still choosing), or a collection or
  // hold, which ships nothing — the sourcing policy needs to know that to
  // settle its default without an address (#85). The preview prices it as
  // `deliverySelectionBody` sends it, so that is what the price depends on.
  const previewDelivery =
    state.delivery &&
    (state.delivery.deliveryAddressId != null ||
      hasSplitAddress(state.delivery) ||
      state.delivery.mode === "collect" ||
      state.delivery.mode === "hold")
      ? state.delivery
      : null;
  const [pricePreviewRefreshKey, setPricePreviewRefreshKey] = useState(0);
  const [pricing, setPricing] = useState<PricingState>({ status: "loading" });
  // The versions: the count is the item's `Versions` field; the split and entered quantities ride beside it. The
  // control previews the backend's rule and the backend's own field errors for them are bound beside its inputs.
  const versionsValue: VersionsValue = {
    count: versionCountOf(state.custom[VERSIONS_FIELD_KEY]),
    split: state.versionSplit,
    entered: state.versionsEntered,
  };
  const versionStep = quantityStepOf(item.quantityBreaks.map((b) => b.qty));
  const versionsUnsettled = versionsProblem(
    versionsValue,
    state.quantity,
    versionStep,
  );
  const [versionErrors, setVersionErrors] = useState<FieldError[]>([]);
  // What changed in the price options since the last preview the CSR saw —
  // the provisional default settling once delivery is priced, or their chosen
  // option dropping out (journey C). Held until they pick again.
  const [optionsNotice, setOptionsNotice] = useState<PriceOptionsNotice | null>(
    null,
  );
  const lastPriceOptionsRef = useRef<CataloguePriceOptions | null>(null);
  useEffect(() => {
    if (pricing.status !== "ready") return;
    const next = pricing.preview.priceOptions ?? null;
    const chosenKey = state.priceOption
      ? `${state.priceOption.routeCode}:${state.priceOption.tier}`
      : null;
    const notice = priceOptionsNotice(
      lastPriceOptionsRef.current,
      next,
      chosenKey,
    );
    lastPriceOptionsRef.current = next;
    // An option chosen while a route is still pricing starts from the backend's reason and note.
    const suggested = next?.overrideRequirement;
    if (suggested?.suggestedReason && state.routeOverrideReason == null) {
      setState({
        routeOverrideReason: suggested.suggestedReason,
        routeOverrideNote: suggested.suggestedNote ?? "",
      });
    }
    if (notice) {
      setOptionsNotice(notice);
      if (notice.kind === "choice_dropped") {
        setState({
          priceOption: null,
          routeOverrideReason: null,
          routeOverrideNote: "",
        });
      }
    }
    // Only a new preview should re-run this; the choice it reads is the one
    // that preview was priced for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing]);
  // The generation counter every pricing resolution bumps when it starts, and
  // every asynchronous continuation checks before writing `pricing`, so a
  // slow answer for a configuration that has moved on is dropped.
  const pricingGenerationRef = useRef(0);

  // Whether card can pay for this item at all: an item with no VAT treatment
  // set is refused card fail-closed at checkout and on a quote the customer
  // accepts alike. How the customer pays is chosen at checkout, from the
  // payer's methods.
  const vatUnresolved =
    previewCustomerCode !== null && item.vatTreatment == null;

  // Curators (admin/manager) get a direct link to resolve it; other roles
  // are told to ask one.
  const vatUnresolvedNotice = vatUnresolved ? (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
    >
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-700 dark:text-amber-400" />
      <div>
        <p className="font-medium">
          VAT treatment not set on this item — card payment is unavailable.
        </p>
        {canOpenProvenanceLinks ? (
          <Link
            to={paths.objectRecord({
              objectId: "catalogue.catalogue_item",
              recordKey: item.id,
            })}
            className="mt-1 inline-block underline hover:no-underline"
          >
            Set it on the catalogue item
          </Link>
        ) : (
          <p className="mt-1 text-amber-900/80 dark:text-amber-200/80">
            Ask an admin to set it.
          </p>
        )}
      </div>
    </div>
  ) : null;

  // Custom-value price ladder (design doc "2c"). A preset-only configuration
  // needs none of this: every (variant, quantity) pair is already cached, so
  // the price-preview effect below resolves the whole ladder in one call. A
  // genuine custom value has nothing cached — `resolveCustomValueLadderConfiguration`
  // folds every active custom field into one request-ready configuration once
  // *all* of them are locally valid (fires no request while any is still
  // incomplete or invalid), and the hook then warms the manufacturing cost
  // for every quantity rung: debounced, bounded concurrency, generation-guarded.
  // It never computes or renders a price itself — landing a rung just bumps
  // `pricePreviewRefreshKey`, so the ladder always re-reads a real backend
  // price off the now-warmer cache, never a client-side derivation.
  const {
    configuration: customLadderConfiguration,
    fieldErrors: customValueErrors,
  } = resolveCustomValueLadderConfiguration(item.fields, state);
  const ladderQuantities = useMemo(
    () => item.quantityBreaks.map((b) => b.qty),
    [item.quantityBreaks],
  );
  const customValueLadder = useCatalogueCustomValueLadder({
    itemId: item.id,
    configuration: customLadderConfiguration,
    quantities: ladderQuantities,
    costFn: (id, req) => adapter.costCatalogueConfiguration(id, req),
    onRungSettled: () => setPricePreviewRefreshKey((k) => k + 1),
  });
  const customValueLadderSettledCount = customValueLadder.rungs.filter(
    (r) => r.status === "settled",
  ).length;
  const customValueLadderFailedCount = customValueLadder.rungs.filter(
    (r) => r.requestError,
  ).length;

  // Every input the resolved price depends on, as one stable string. Anything
  // added to the request below must be added here too, or the page will show a
  // price resolved for different inputs than the ones on screen.
  const pricingKey = [
    item.id,
    state.quantity,
    previewCustomerCode ?? "",
    JSON.stringify(previewDelivery && deliverySelectionBody(previewDelivery)),
    JSON.stringify(state.values),
    JSON.stringify(state.multi),
    JSON.stringify(state.custom),
    JSON.stringify(versionsRequestFields(versionsValue)),
    versionsUnsettled?.code ?? "",
    // Not itself pricing-relevant input, but the effect must re-run the
    // instant the last unresolved field clears so pricing starts rather
    // than waiting for some other field to also change.
    reorderUnresolvedFieldKeys.length,
    JSON.stringify(state.priceOption),
    state.speedCode ?? "default",
    state.neededBy ?? "none",
    state.neededByIsHard,
  ].join("|");
  useEffect(() => {
    // Every resolution bumps the generation before doing anything else,
    // including the early-return branches below: even a synchronous
    // "context" / "blocked" state must invalidate whatever async work an
    // earlier run left in flight.
    const generation = ++pricingGenerationRef.current;
    if (!itemAvailable) {
      setPricing({ status: "context" });
      return;
    }
    // Order again pre-fill only: block price-preview entirely while any field is
    // still tier-"unresolved" (brief §16 — "no price shows until every field
    // resolves, so nothing can misprice"). No fetch, no stale price left
    // behind from a previous configuration.
    if (reorderUnresolvedFieldKeys.length > 0) {
      setPricing({
        status: "blocked",
        unresolvedCount: reorderUnresolvedFieldKeys.length,
      });
      return;
    }
    // Versions whose quantities cannot be shared yet (an even split that does not fit, entered quantities that do
    // not add up): the backend would refuse the request, so it is not sent; the control says what to fix.
    setVersionErrors([]);
    if (versionsUnsettled !== null) {
      setPricing({ status: "versions_unsettled" });
      return;
    }
    // Live costing paused: priced like any other item. Its in-house route
    // has no price unless one is held, so it is not an option and another
    // route is chosen (order to invoice doc 107 §2a); when no route prices,
    // the page reads "Price unavailable" as for any item (§2b).
    // Clear first: while a new resolution is in flight the old price is stale
    // by definition, and a stale price is a wrong price.
    setPricing({ status: "loading" });
    const request = {
      configuration: buildCatalogueConfiguration(state),
      quantity: state.quantity,
      speedCode: state.speedCode,
      customerCode: previewCustomerCode,
      delivery: previewDelivery,
      priceOption: state.priceOption,
      neededBy: state.neededBy,
      neededByIsHard: state.neededByIsHard,
      ...versionsRequestFields(versionsValue),
    };

    /**
     * Selected quantity first, then the rest of the ladder.
     *
     * Every rung is priced from scratch, so one preview used to pay for ten
     * quantities before the screen could show the one number the orderer is
     * waiting for. The first call asks for the selected quantity alone and
     * renders it; the second asks for the whole ladder and fills the rest in.
     *
     * Sequential, not concurrent, and that is the point: the API runs a
     * single worker, so firing both at once would queue the headline behind
     * the ladder and buy nothing. Both answers are generation-guarded
     * against `pricingGenerationRef`: the next run bumps it before starting,
     * so a response for a configuration that has moved on is dropped rather
     * than painted.
     */
    // A route still pricing is asked again until it answers (live supplier pricing §6.3): its name shows
    // meanwhile, beside the price when another route has priced.
    const askingFor = (ladder: boolean) => () =>
      askUntilPriced(
        () =>
          adapter.getCatalogueItemPricePreview(item.id, { ...request, ladder }),
        {
          pendingIn: (answer) => answer.priceOptions?.pending ?? [],
          isCurrent: () => pricingGenerationRef.current === generation,
          onPending: (pending, answer) => {
            if (pricingGenerationRef.current !== generation) return;
            setPricing(
              answer
                ? { status: "ready", preview: answer, ladderStatus: "loading" }
                : { status: "pending", pending },
            );
          },
        },
      );

    async function priceSelectedThenLadder() {
      let headline;
      try {
        headline = await askingFor(false)();
      } catch (err) {
        // The selected quantity is the price the orderer asked for. Losing it
        // is the whole preview failing: no route prices it. A refusal naming
        // the versions' own fields is shown beside them instead.
        if (pricingGenerationRef.current === generation) {
          const fieldErrors = extractFieldErrors(err);
          const messages = versionsFieldMessages(fieldErrors);
          if (messages.count || messages.split || messages.quantities) {
            setVersionErrors(fieldErrors);
            setPricing({ status: "versions_unsettled" });
          } else {
            setPricing({ status: "error" });
          }
        }
        return;
      }
      if (pricingGenerationRef.current !== generation) return;
      setPricing({
        status: "ready",
        preview: headline,
        ladderStatus: "loading",
      });

      try {
        const full = await askingFor(true)();
        if (pricingGenerationRef.current === generation) {
          setPricing({ status: "ready", preview: full, ladderStatus: "ready" });
        }
      } catch {
        // The headline is real and stays on screen. Only the quantities this
        // call would have filled go unpriced — a browse comparison the
        // orderer did not ask for is not worth taking the price down over.
        if (pricingGenerationRef.current === generation) {
          setPricing({
            status: "ready",
            preview: headline,
            ladderStatus: "failed",
          });
        }
      }
    }

    void priceSelectedThenLadder();
    // No cleanup needed: the next run of this effect bumps
    // `pricingGenerationRef` itself before starting any work.
    // pricingKey is a stable string over every pricing-relevant input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, item.id, itemAvailable, pricingKey, pricePreviewRefreshKey]);

  const pricePreview = pricing.status === "ready" ? pricing.preview : null;
  const priceOptions: CataloguePriceOptions | null =
    pricePreview?.priceOptions ?? null;
  // Who delivers when the chosen option's price includes delivery.
  const chosenPriceOption = priceOptions
    ? findOption(priceOptions, priceOptions.selectedKey)
    : null;
  const deliveringSupplier =
    chosenPriceOption?.delivery.kind === "included"
      ? chosenPriceOption.routeLabel
      : null;

  // The displayed total is the resolved quote plus the CSR's own explicit
  // adjustment — the same figure the basket line is priced at, so what
  // the rep reads is what the customer is quoted. Nothing else moves it, and
  // the ladder composes its rows through the same `quotedPence`, so the
  // selected row and this total are one number.
  // A hand-linked precedent is folded into the levels the picker shows, so
  // the linked job/estimate is selectable like an engine-recognised one.
  const presentedLevels =
    pricePreview == null
      ? undefined
      : state.linkedPrecedent
        ? withLinkedPrecedent(
            pricePreview.priceLevels ??
              policyOnlyLevels(pricePreview.quotedPence, state.quantity),
            state.linkedPrecedent,
            state.quantity,
          )
        : pricePreview.priceLevels;
  const levelInForce =
    pricePreview != null
      ? levelPence(
          pricePreview.quotedPence,
          presentedLevels,
          state.priceLevel,
          state.customPricePence,
          true,
        )
      : null;
  const displayedQuotedPence = levelInForce?.pence ?? null;
  // What the write path records: the chosen price as a delta from policy.
  const priceAdjustmentPence =
    pricePreview != null && displayedQuotedPence != null
      ? displayedQuotedPence - pricePreview.quotedPence
      : 0;
  const effectiveLevel: PriceLevelKind =
    levelInForce?.level ?? presentedLevels?.suggested ?? "policy";
  const displayedPerUnit =
    displayedQuotedPence != null && state.quantity > 0
      ? displayedQuotedPence / 100 / state.quantity
      : 0;

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="space-y-4">
        <Link
          to={backToCatalogue}
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)]"
        >
          <ArrowLeft className="size-4" /> Back to catalogue
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex size-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Icon className="size-6" />
            </div>
            <div>
              <h1>{item.name}</h1>
              <div className="text-sm text-muted-foreground">
                {item.productLabel}
                {item.specSummary && <span> · {item.specSummary}</span>}
              </div>
              {migratedLine && (
                <p className="text-sm text-muted-foreground">{migratedLine}</p>
              )}
              {item.tharsternProvenance && (
                <p className="text-sm text-muted-foreground">
                  {cameFromTharsternText(item.tharsternProvenance)}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <Badge variant={TONE_VARIANT[badge.tone]}>{badge.label}</Badge>
            {/* Curators only (admin + manager, the catalogue:manage tier):
                both targets are curation pages a CSR or sales rep would be
                bounced from, and a link that lands on a forbidden page is
                worse than no link. */}
            {canOpenProvenanceLinks && (
              <div className="flex items-center gap-2">
                <Link
                  to={paths.objectRecord({
                    objectId: "catalogue.catalogue_item",
                    recordKey: item.id,
                  })}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  title="Edit pricing &amp; quantity breaks"
                >
                  <Pencil className="size-3" />
                  Edit item
                </Link>
                {/* The price group that priced the preview: the customer's (an item names none). */}
                {pricePreview?.priceGroupId && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <Link
                      to={paths.settingsPricingEdit({
                        groupId: pricePreview.priceGroupId,
                      })}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      title="Open price group"
                    >
                      <ExternalLink className="size-3" />
                      {pricePreview?.priceGroupName || "Price group"}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CatalogueCustomerContext
        customer={context.customer}
        onChange={context.selectCustomer}
        onCreateCustomer={
          canCreateCustomer
            ? () => {
                setState({ customerMode: "new" });
                setCreateCustomerOpen(true);
              }
            : undefined
        }
      />
      {context.loading ? (
        <p role="status">Checking catalogue availability and pricing…</p>
      ) : context.failed ? (
        <div role="alert" className="space-y-2">
          <p>
            Catalogue availability could not be checked. Your product choices
            are retained.
          </p>
          <Button variant="outline" onClick={context.retry}>
            Try again
          </Button>
        </div>
      ) : !itemAvailable ? (
        <p role="alert">
          This item is not available in the selected customer context. Your
          choices are retained for reference. Select a matching customer or
          return to the catalogue to choose an available item.
        </p>
      ) : customerChanged ? (
        <p role="status" className="text-sm text-muted-foreground">
          Customer changed. Product choices are retained; check contact and
          delivery details again.{" "}
          {pricing.status === "ready"
            ? "Pricing has been refreshed below."
            : "Review the pricing status below."}
        </p>
      ) : null}

      {/* pb leaves a safe area so the sticky checkout (and its CTA) clears the
          fixed bottom-right Feedback button. */}
      <div className="grid gap-6 lg:grid-cols-5 items-start pb-24">
        {/* LHS — configure the item */}
        <div className="lg:col-span-3 space-y-5">
          {/* Contact selection uses the ordering customer from the catalogue. */}
          <ContactSection
            key={`contact:${context.customer?.code ?? "standard"}`}
            createCustomerOpen={createCustomerOpen}
            onCreateCustomerOpenChange={(open) => {
              setCreateCustomerOpen(open);
              if (!open) setState({ customerMode: "existing" });
            }}
            state={state}
            setState={setState}
            onCustomerSaved={(created) => {
              setCreateCustomerOpen(false);
              // Straight into the existing-customer path, on its catalogue
              // code: a customer created in CIS has no Tharstern code, so its
              // CUS- code. It has no contacts yet, so the contact starts as a
              // new one to type in (a card order needs one at checkout).
              setState({
                customerMode: "existing",
                selectedCustomer: {
                  code:
                    created.tharstern_customer_code ?? created.customer_code,
                  name: created.name,
                },
                contactMode: "new",
                newContactName: "",
                newContactEmail: "",
              });
            }}
          />

          {/* Delivery — priced here, since a quote must carry its delivery cost (spec 21 DECISION 3) and an
              order line delivers somewhere. Shown once a customer is chosen (address book lookup needs customer code). */}
          <DeliverySection
            key={`delivery:${context.customer?.code ?? "standard"}`}
            customerCode={
              state.customerMode === "existing" && state.selectedCustomer
                ? state.selectedCustomer.code
                : ""
            }
            // The customer spine's address book (customer-address-spine 03
            // §15): searched and paged server-side, and a captured address is
            // created in CIS at once. A customer just created here is in the
            // spine already, its account address the default delivery.
            addressBook="customer_spine"
            productionSite="Buckingham - Osier Way"
            allowedModes={["ship", "multi", "collect", "hold"]}
            // The courier list, the total row and Collect read the preview's
            // delivery (doc 104 §9.1).
            pricedDelivery={pricePreview?.delivery ?? null}
            deliveringSupplier={deliveringSupplier}
            // A route withheld because its delivery isn't priced, with the agreed price that offers it (doc 107).
            deliveryNotPricedWarnings={
              pricePreview?.deliveryNotPricedWarnings ?? []
            }
            // Multi-destination (doc 106 §3): its rows add up to the line's
            // quantity, each reading its own delivery.
            quantity={state.quantity}
            destinationDeliveries={pricePreview?.destinationDeliveries ?? []}
            onChange={(delivery) => setState({ delivery })}
          />

          {/* Order again provenance (catalogue-reorder brief §16) — only present
              when this landing came from a reorder pick. */}
          {preparedReorder && (
            <ReorderProvenanceBanner prepared={preparedReorder} />
          )}

          {/* Quantity ladder */}
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Quantity</div>
              {/* A custom value has no cached price yet — each row fills in
                  as its manufacturing cost lands, never blocking the others
                  or the CTA (design doc "2c"). */}
              {customValueLadder.warming && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" />
                  Pricing {customValueLadderSettledCount} of{" "}
                  {customValueLadder.rungs.length} quantities…
                </div>
              )}
            </div>
            {/* The request itself failing (item not found, access denied,
                network) is different from Tharstern rejecting the
                configuration — that lands as a normal settled cost with its
                own status="failed" and a per-row "not costed" cell (the
                failed rung never lands in pricing.preview.quantityLadder, so
                its row already reads "not costed" with no extra marking
                needed here). This is the request never completing at all;
                it used to be silently dropped (`requestError` was computed
                and never read), which is how a raw fetch failure could
                surface as unexplained page chrome instead of a designed
                message. Always a translated sentence — never
                `rung.requestError` itself, which can carry a raw HTTP status
                line. Every rung down is a genuine outage worth an alert;
                some rungs down is routine flakiness on an otherwise-working
                ladder, so it gets a quiet note instead (doc 68 §8 item 15). */}
            {!customValueLadder.warming &&
              customValueLadderFailedCount > 0 &&
              (customValueLadderFailedCount ===
              customValueLadder.rungs.length ? (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-[color:var(--color-error,red)]/40 bg-[color:var(--color-error,red)]/5 px-3 py-2 text-xs"
                >
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[color:var(--color-error,red)]" />
                  <span>
                    Couldn&rsquo;t reach live pricing for this custom
                    specification. Change the quantity or specification to
                    retry.
                  </span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Live pricing unavailable for {customValueLadderFailedCount} of{" "}
                  {customValueLadder.rungs.length} quantities — change the
                  quantity or specification to retry.
                </p>
              ))}
            <ItemQuantityLadder
              quantityBreaks={item.quantityBreaks.map((b) => b.qty)}
              selection={state}
              onSelect={setState}
              pricing={pricing}
            />
          </Card>

          {/* Specification — dense spec sheet */}
          {(jobFields.length > 0 || hasSections) && (
            <Card className="p-5">
              <ItemSpecification
                fields={item.fields}
                selection={state}
                onChange={setState}
                errors={customValueErrors}
                lineVersions={{
                  value: versionsValue,
                  quantity: state.quantity,
                  quantityStep: versionStep,
                  errors: versionsFieldMessages(versionErrors),
                  onChange: (next) =>
                    setState({
                      versionSplit: next.split,
                      versionsEntered: next.entered,
                    }),
                }}
                reorderChipFor={(key) => (
                  <ReorderFieldChip
                    resolution={reorderFieldResolutions?.get(key)}
                  />
                )}
              />
            </Card>
          )}

          {/* The next start number of a numbered item (NCR start-number
              continuation): Order again's proposal when there is one, else the
              item's own. Staff confirm it; Apply only fills the field. */}
          {startNumberProposal || itemStartNumber.state?.is_series ? (
            <StartNumberPanel
              proposal={startNumberProposal}
              canApply={hasStartNumberField}
              applied={
                startNumberProposal != null &&
                state.custom[START_NUMBER_FIELD_KEY] ===
                  startNumberProposal.next_start
              }
              onApply={() =>
                startNumberProposal &&
                setState({
                  custom: {
                    ...state.custom,
                    [START_NUMBER_FIELD_KEY]: startNumberProposal.next_start,
                  },
                })
              }
              opening={
                itemStartNumber.state?.is_series
                  ? {
                      saving: itemStartNumber.saving,
                      error: itemStartNumber.error,
                      onSave: itemStartNumber.saveOpening,
                    }
                  : undefined
              }
            />
          ) : null}
        </div>

        {/* RHS — checkout: a distinct, elevated panel vs the flat config cards.
            Not sticky: it scrolls with the page so its buttons settle clear of
            the fixed bottom-right Feedback widget (the page pb reserves room). */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="overflow-hidden border-border shadow-md">
            <div className="p-6 space-y-5">
              {/* Spec summary (left) + a thumbnail of the thing being ordered (right) */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {state.quantity.toLocaleString()} ·{" "}
                    {recap || item.specSummary || item.productLabel}
                  </div>
                </div>
                <div className="shrink-0 overflow-hidden rounded-md border border-border">
                  <CataloguePreview name={item.name} icon={Icon} {...preview} />
                </div>
              </div>

              {/* Speed — the customer's one choice of how soon it arrives
                  (d64), each with its date and price, and the route options
                  behind each. Distinct from the courier Delivery section
                  above. */}
              {pricePreview && (
                <SpeedList
                  speeds={pricePreview.speeds}
                  priceOptions={priceOptions}
                  neededBy={state.neededBy}
                  neededByIsHard={state.neededByIsHard}
                  notice={optionsNotice}
                  onNeededByChange={(neededBy) => {
                    setOptionsNotice(null);
                    setState({
                      neededBy,
                      ...(neededBy == null ? { neededByIsHard: false } : {}),
                    });
                  }}
                  onNeededByIsHardChange={(neededByIsHard) =>
                    setState({ neededByIsHard })
                  }
                  onSelectSpeed={(speedCode) => {
                    setOptionsNotice(null);
                    setState({
                      speedCode,
                      priceOption: null,
                      routeOverrideReason: null,
                      routeOverrideNote: "",
                    });
                  }}
                  onSelectOption={(o, speedCode) => {
                    setOptionsNotice(null);
                    setState({
                      speedCode,
                      priceOption: { routeCode: o.routeCode, tier: o.tier },
                    });
                  }}
                  overrideReason={state.routeOverrideReason}
                  overrideNote={state.routeOverrideNote}
                  onOverrideReasonChange={(reason) => {
                    setState({ routeOverrideReason: reason });
                  }}
                  onOverrideNoteChange={(note) => {
                    setState({ routeOverrideNote: note });
                  }}
                />
              )}

              {/* Price. Four mutually exclusive states, and only one of them
                  shows a number: a screen that cannot price says so rather
                  than showing something that looks like a price. */}
              {pricing.status === "blocked" && (
                <div
                  role="status"
                  className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm dark:bg-amber-900/10"
                >
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400" />
                  <div>
                    <div className="font-medium text-amber-900 dark:text-amber-200">
                      {pricing.unresolvedCount === 1
                        ? "1 field needs a current value before this can be priced"
                        : `${pricing.unresolvedCount} fields need a current value before this can be priced`}
                    </div>
                    <div className="text-xs text-amber-900/80 dark:text-amber-200/80">
                      Resolve the "was: X" fields above — nothing can misprice
                      off an incomplete spec.
                    </div>
                  </div>
                </div>
              )}

              {pricing.status === "versions_unsettled" && (
                <div
                  role="status"
                  className="border-t border-border pt-4 text-sm text-muted-foreground"
                  data-testid="price-versions-unsettled"
                >
                  No price yet: settle the quantity of each version above.
                </div>
              )}

              {pricing.status === "loading" && (
                <div
                  className="flex items-center gap-2 border-t border-border pt-4 text-sm text-muted-foreground"
                  aria-live="polite"
                >
                  <Loader2 className="size-4 animate-spin" />
                  Pricing this configuration…
                </div>
              )}

              {pricing.status === "pending" && (
                <div
                  className="space-y-1 border-t border-border pt-4 text-sm text-muted-foreground"
                  role="status"
                  aria-live="polite"
                  data-testid="price-pending"
                >
                  <div className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Pricing this configuration…
                  </div>
                  <ul className="pl-6">
                    {pricing.pending.map((route) => (
                      <li key={route.routeCode}>{pendingRouteLine(route)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No route prices this configuration, a paused item's
                  included: a route that holds no price is not an option, and
                  nothing asks Tharstern for one on demand (order to invoice
                  doc 107 §2a, §2b). */}
              {pricing.status === "error" && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-[color:var(--color-error,red)]/40 bg-[color:var(--color-error,red)]/5 px-3 py-2.5 text-sm"
                >
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[color:var(--color-error,red)]" />
                  <div className="flex-1">
                    <div className="font-medium">Price unavailable</div>
                    <div className="text-xs text-muted-foreground">
                      No route can price this configuration, so no price is
                      shown. Change the specification or quantity and retry. Do
                      not quote from memory.
                    </div>
                  </div>
                </div>
              )}

              {pricing.status === "ready" && displayedQuotedPence != null && (
                <>
                  {/* The pricing hierarchy: contracted → last ordered (suggested
                      when it still clears the floor) → last quoted → policy
                      → custom. The chosen
                      level IS the headline; the write path records it as a
                      delta from policy plus the level and reason. */}
                  <PriceLevelPicker
                    levels={
                      presentedLevels ??
                      policyOnlyLevels(
                        pricing.preview.quotedPence,
                        state.quantity,
                      )
                    }
                    priceGroupName={pricing.preview.priceGroupName}
                    selected={effectiveLevel}
                    customPence={state.customPricePence}
                    customReason={state.priceAdjustmentReason}
                    manufacturingCostPence={
                      pricing.preview.manufacturingCostPence
                    }
                    onSelect={(level) => setState({ priceLevel: level })}
                    onCustomChange={({ pence, reason }) =>
                      setState({
                        priceLevel: "custom",
                        customPricePence: pence,
                        priceAdjustmentReason: reason,
                      })
                    }
                    onSearchCandidates={
                      previewCustomerCode
                        ? (query) =>
                            adapter.searchPrecedentCandidates(
                              previewCustomerCode,
                              query,
                            )
                        : undefined
                    }
                    onLinkPrecedent={(candidate) => {
                      // Optimistic: the picker folds the link in at once; the
                      // record is what makes the next quote resolve.
                      setState({
                        linkedPrecedent: candidate,
                        priceLevel: null,
                      });
                      const specIdentity =
                        pricing.preview.precedent?.spec_identity ?? null;
                      if (previewCustomerCode && specIdentity) {
                        void adapter.linkPrecedent({
                          customerCode: previewCustomerCode,
                          specIdentity,
                          candidate,
                        });
                      }
                    }}
                  />
                  <PriceBreakdownPanel
                    preview={pricing.preview}
                    quotedTotalPence={displayedQuotedPence}
                    priceAdjustmentPence={priceAdjustmentPence}
                    perUnitLabel={perUnitLabel(displayedPerUnit)}
                    deliveryMode={state.delivery?.mode ?? null}
                    quantity={state.quantity}
                    canOpenProvenanceLinks={canOpenProvenanceLinks}
                    costLineage={
                      <CostFreshnessIndicator
                        itemId={item.id}
                        quantity={state.quantity}
                        preview={pricing.preview}
                        onRefreshed={() =>
                          setPricePreviewRefreshKey((k) => k + 1)
                        }
                      />
                    }
                  >
                    {priceAdjustmentPence !== 0 && (
                      <BreakdownLine
                        label={
                          effectiveLevel === "contracted"
                            ? "Contracted rate vs policy"
                            : effectiveLevel === "last_ordered"
                              ? "Held at last ordered price"
                              : effectiveLevel === "similar_ordered"
                                ? "Priced from a similar order"
                                : effectiveLevel === "last_quoted"
                                  ? "Matched to last quote"
                                  : "Custom price vs policy"
                        }
                        amount={Math.abs(priceAdjustmentPence) / 100}
                        kind={priceAdjustmentPence < 0 ? "discount" : "add"}
                      />
                    )}
                  </PriceBreakdownPanel>
                </>
              )}

              <div className="space-y-2 pt-2">
                {/* A repeatable item's order reuses its artwork: no files are
                    asked for (AW3c-4). */}
                {itemArtwork && (
                  <RepeatableItemArtworkNote
                    artwork={itemArtwork}
                    label="Artwork"
                  />
                )}
                {vatUnresolvedNotice}
                {/* Quoting and ordering are One's (Ben, 19 Oct): the draft quote is the staff quote builder, and
                    Classic keeps the price. */}
                <p className="text-center text-xs text-muted-foreground">
                  To quote or order this item, add it to a draft quote in{" "}
                  <Link
                    to={paths.objectRecord({
                      objectId: "catalogue.catalogue_item",
                      recordKey: item.id,
                    })}
                    className="text-[color:var(--color-primary)] hover:underline"
                  >
                    One
                  </Link>
                  .
                </p>
                {pricing.status === "error" && (
                  <div className="text-center text-xs text-muted-foreground">
                    Cannot quote or order without a resolved price
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
